import { z } from "zod/v4";

import { PluginTrafficConfigType_2023_0_20 } from "../../schemas/plugin/config/experiments";
import { Optional } from "../../utils";
import { AudienceFilterTypeList, ExpressionNode } from "../audiences";
import {
  ExperiencePageTargetingFilterTypeList,
  TrafficConfigTargetingFilterList,
} from "../experience-page-targeting";
import { UrlMatchList } from "../url-match";

// for backwards compat only
export const TrafficConfigActionList = [
  "campaignInclude",
  "campaignExclude",
  "campaignUnassigned",
  "assignTestGroup",
  "randomTestGroup",
  "excludeFromExperience",
  "leaveUnassigned",
  "experienceInclude",
  "experienceExclude",
  "experienceUnassigned",
  "assignVariation",
  "randomVariation",
  "leaveUnassigned",
  "",
] as const;

export const TrafficConfigExcludeCurrencyDtoSchema = z.object({
  exclude: z.boolean(),
  currency: z.string().nullable(),
  country: z.string().optional(),
  region: z.string().optional(),
  city: z.string().optional(),
});

export const TrafficConfigQueryParamsDtoSchema = z.object({
  id: z.uuid(),
  priority: z.number().int(),
  filterType: z.enum(AudienceFilterTypeList).default("utm"),
  key: z.string(),
  value: z.string(),
  filter: z.enum(TrafficConfigTargetingFilterList),
  action: z.preprocess(
    (val) => (val ? val : ""),
    z.enum(TrafficConfigActionList)
  ),
  expression: z.array(ExpressionNode).optional().nullable(),
  testGroupId: z.uuid().nullish(),
  excludeFromAnalytics: z.boolean().default(false),
});

export const PageTargetingDtoSchema = z.object({
  id: z.uuid(),
  filter: z.enum(UrlMatchList).nullish(),
  filterType: z.enum(ExperiencePageTargetingFilterTypeList).default("url"),
  value: z.string(),
  testGroupId: z.uuid().nullish(),
  order: z.number(),
});

export type PageTargetingType = z.infer<typeof PageTargetingDtoSchema>;

export interface TrafficLikeObject {
  isExcludedByMarket(
    trafficConfig?: PluginTrafficConfigType_2023_0_20,
    useBrowserLocale?: boolean
  ): {
    expected: string;
    found: string | false;
  };

  isUnassigned(id: string): boolean;

  unassign(id: string, logLocation?: string): void;

  isPermaExcluded(id: string): boolean;

  permaExclude(id: string): void;

  isExcluded(id: string): boolean;

  exclude(id: string): void;

  unexclude(id: string): void;

  isWholesale(
    trafficConfig?: PluginTrafficConfigType_2023_0_20
  ): string | false;

  hasHistory(id: string): boolean;

  getHistory(id: string): string | undefined;

  shouldAssign(
    id: string,
    trafficConfig?: PluginTrafficConfigType_2023_0_20
  ): boolean;

  getCampaignIdParam(): string | null;
}

export const TrafficConfigDtoSchema = z.object({
  id: z.uuid(),
  enabled: z.boolean(),
  excludeCurrency: TrafficConfigExcludeCurrencyDtoSchema,
  wholesale: z.boolean(),
  elseAction: z.enum(TrafficConfigActionList).nullish().default(""),
  elseTestGroupId: z.uuid().nullish(),
  elseTestGroupName: z.string().nullish(),
  elseExcludeFromAnalytics: z.boolean().default(false),
  queryParams: z
    .optional(TrafficConfigQueryParamsDtoSchema.array())
    .default([]),
  pageTargeting: z.optional(PageTargetingDtoSchema.array()).default([]),
});

export type TrafficConfigDto = z.infer<typeof TrafficConfigDtoSchema>;
export type PutTrafficConfigDto = Optional<TrafficConfigDto, "id">;
